<template>
    <div class="form-wrapper">
        <img class="form-img" src="~@/assets/wc_lock.png" />
        <div class="form-title">
            Create a new game
        </div>
        <form v-on:submit.prevent="onCreateGame">
            <div class="form-control-wrapper">
				   <div class="form-control-with-count">
						<input
							class="form-control"
							v-model="form.roomName"
							placeholder="Room Name (optional)"
							name="roomName"
							autocomplete="off"
							:maxlength="MAX_ROOM_NAME_LENGTH"
							:disabled="formReadOnly"
							@focus="isRoomNameFocused = true"
    						@blur="isRoomNameFocused = false"
						/>
						<span class="char-count" v-if="isRoomNameFocused">
							{{ form.roomName.length }}/{{ MAX_ROOM_NAME_LENGTH }}
						</span>
				   </div>

                <div class="form-error" v-if="errors.roomName">
                    {{ errors.roomName }}
                </div>
            </div>
            <div
                class="form-control-wrapper select-wrapper"
            >
                <select
                    class="form-control select"
                    v-model="form.numOfPlayers"
                    name="numOfPlayers"
                    :disabled="formReadOnly"
                >
                    <option value="2">2 Players</option>
                    <option value="4">4 Players</option>
                </select>
                <fa-icon icon="sort-down" class="icon" />
            </div>
            <div class="form-group">
                <div class="form-control-wrapper select-wrapper select-with-icon-wrapper">
                    <select
                        class="form-control select"
                        :value="timeLimit"
                        name="timeLimit"
                        :disabled="formReadOnly"
                        @change="setTimeLimit($event)"
                    >
                        <option
                            v-for="option in timeLimitOptions"
                            :key="`time-limit-option-${option.value}`"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </option>
                    </select>
                    <fa-icon icon="sort-down" class="icon" />
                    <fa-icon icon="clock" class="icon option-icon time-limit-icon" />
                </div>
                <div class="form-control-wrapper select-wrapper select-with-icon-wrapper">
                    <select
                        class="form-control select"
                        :value="ranked"
                        name="ranked"
                        :disabled="formReadOnly"
                        @change="setRanked($event)"
                    >
                        <option value="true">Ranked</option>
                        <option value="false">Unranked</option>
                    </select>
                    <fa-icon icon="sort-down" class="icon" />
                    <fa-icon icon="trophy" class="icon option-icon ranked-icon" v-if="ranked" />
                    <fa-icon icon="smile" class="icon option-icon unranked-icon" v-if="!ranked" />
                </div>
            </div>
            <div class="form-control-wrapper select-wrapper">
                <ranks-dropdown
                    :value="allowedRanks"
                    :readonly="formReadOnly"
                    @change="setAllowedRanks($event)"
                />
            </div>
            <div class="form-control-wrapper">
                <span
                    class="form-control room-password-input"
                    :class="{ readonly: formReadOnly }"
                    :contenteditable="(!formReadOnly).toString()"
                    @input="onRoomPasswordInput"
                >
                </span>
                <div class="form-error" v-if="errors.password">
                    {{ errors.password }}
                </div>
            </div>
            <!-- <div class="form-control-wrapper checkbox-wrapper">
				<button
                    class="btn btn-block btn-outline ai-mode-btn"
                    @click="(event) => setAiModeFromCheckbox(event, !aiMode)"
					type="button"
                >
                    <span class="btn-text">Play vs AI</span>
					<switch-toggle
                        :icon="aiMode ? 'check' : ''"
                        :checked="aiMode"
                        :callback="() => {}"
                        :altColors="true"
                    />
                </button>
            </div> -->
            <div class="form-control-wrapper">
                <input
					class="btn btn-primary btn-block"
					type="submit"
					value="Create"
				/>
            </div>
        </form>
    </div>
</template>

<script>
const MAX_ROOM_NAME_LENGTH = 20;

export default {
    name: 'CreateRoomForm',
	created() {
        if (this.$store.state.loginState.aiEnabled || this.$store.state.appState.gameSettings.aiMode) {
            this.$store.dispatch('setAiMode', false);
        }
	},
    data() {
        return {
            form: {
                roomName: '',
                numOfPlayers: '2',
                password: '',
                allowedRanks: [],
            },
            errors: {
                roomName: null,
                numOfPlayers: null,
                password: null,
                timeLimit: null,
                allowedRanks: null,
            },
			['MAX_ROOM_NAME_LENGTH']: MAX_ROOM_NAME_LENGTH,
			isRoomNameFocused: false,
        };
    },
    watch: {
        ['form.numOfPlayers'](current, prev) {
            if (parseInt(prev, 10) === 2 && parseInt(current, 10) === 4 && this.settings.blitz) {
                this.setTimeLimit({
                    target: {
                        value: '3',
                    },
                });
            }
        },
    },
    computed: {
		aiEnabled() {
            return this.$store.state.loginState.aiEnabled;
        },
        settings() {
            return this.$store.state.appState.gameSettings;
        },
        timeLimit() {
            if (this.settings.blitz) {
                return `blitz_${this.settings.timeLimit}`;
            }
            return this.settings.timeLimit;
        },
        ranked() {
            return this.settings.ranked;
        },
        aiMode() {
            return this.settings.aiMode;
        },
        formReadOnly() {
            return this.aiMode;
        },
        allowedRanks() {
            return this.settings.allowedRanks;
        },
        timeLimitOptions() {
            let options = [
                {
                    value: '2',
                    label: '2 min/turn',
                },
                {
                    value: '3',
                    label: '3 min/turn',
                },
                {
                    value: '5',
                    label: '5 min/turn',
                },
                {
                    value: '10',
                    label: '10 min/turn',
                },
                {
                    value: '15',
                    label: '15 min/turn',
                },
                {
                    value: '30',
                    label: '30 min/turn',
                },
                {
                    value: 'blitz_2',
                    label: '2 min (blitz)',
                },
                {
                    value: 'blitz_3',
                    label: '3 min (blitz)',
                },
                {
                    value: 'blitz_5',
                    label: '5 min (blitz)',
                },
            ];

            if (parseInt(this.form.numOfPlayers, 10) !== 2) {
                options = options.filter(option => !option.value.startsWith('blitz'));
            }

            return options;
        },
    },
    methods: {
        async onCreateGame() {
            const form = { ...this.form, ...this.settings };
            this.$store.dispatch('setSoundReady');
            this.$store.dispatch('createGame', form);
        },
        onRoomPasswordInput(event) {
            if (this.formReadOnly) {
                return;
            }
            this.form.password = event.target.innerHTML.trim();
        },
        setTimeLimit($event) {
            let value = $event.target.value;
            const blitz = value.startsWith('blitz') ? parseInt(value.split('_').pop(), 10) : null;

            if (blitz) {
                value = value.split('_').pop();
            }

            this.$store.dispatch('setBlitz', blitz);
            this.$store.dispatch('setTimeLimit', parseInt(value, 10));
        },
        setRanked($event) {
            this.$store.dispatch('setRanked', $event.target.value === 'true');
        },
        setAiModeFromCheckbox(event, value) {
			event.stopPropagation();
            this.$store.dispatch('setAiMode', value);
        },
        setAllowedRanks(value) {
            this.$store.dispatch('setAllowedRanks', value);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .form-wrapper {
        background: $dark-brown;
        padding: 25px;
        border-radius: 4px;
        color: $light-gray;
        width: 285px;
        margin: 0 auto;

        .form-img {
            max-height: 75px;
            max-width: 100%;
            height: auto;
            width: auto;
            margin-bottom: 15px;
        }

        .form-title {
            font-size: 1.2em;
        }

        .room-password-input {
            position: relative;
            max-height: 38px;

            &:not(:empty) {
                font-family: 'SecurityDisc', 'Arial';
                -webkit-text-security: disc;
            }

            &:empty:before {
                content: 'Password (optional)';
                color: $medium-gray-alt;
                display: block;
                pointer-events: none;
            }

            &.readonly {
                background: $disabled-control-bg;
                cursor: not-allowed;

                &:empty:before {
                    color: $disabled-control-text;
                }
            }
        }

        .form-control:disabled {
            background: $disabled-control-bg;
            color: $disabled-control-text;
            -webkit-text-fill-color: $disabled-control-text;
            opacity: 1;
            cursor: not-allowed;
        }

        .form-control:disabled::placeholder {
            color: $disabled-control-text;
            -webkit-text-fill-color: $disabled-control-text;
            opacity: 1;
        }

		.ai-mode-btn {
			display: flex;
			justify-content: space-between;
			align-items: center;
			font-weight: normal;
			padding: 0 12px;
		}

        .select-wrapper {
            position: relative;

            .icon {
                position: absolute;
                color: $dark-blue;
                right: 10px;
                top: 9px;
            }
        }

        .select-with-icon-wrapper {

            .select {
                padding-left: 35px;
            }

            .option-icon {
                position: absolute;
                left: 10px;
                top: 50%;
                transform: translateY(-50%);
                color: $dark-brown;

                &.ranked-icon {
                    font-size: .92em;
                }
            }
        }

        .form-group {

            .form-control-wrapper {

                &:first-of-type {
                    margin-right: 7.5px;
                }

                &:last-of-type {
                    margin-left: 7.5px;
                }
            }
        }

		.form-control-with-count {
			position: relative;

			.form-control {
				padding-right: 48px;
			}

			.char-count {
				position: absolute;
				right: 12px;
				top: 50%;
				transform: translateY(-50%);
				font-size: .75rem;
				color: $dark-gray;
				white-space: nowrap;
				pointer-events: none;
			}
		}

        .form-hint {
            font-size: 0.8em;
            margin-top: 3px;
        }

        .checkbox-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;

            .checkbox-label {
                display: flex;
                align-items: center;
                font-size: 0.95em;
                cursor: pointer;

                input {
                    margin-right: 8px;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-md) {

        .form-wrapper {
            width: 300px;
        }
    }
</style>
